import React, { useEffect, useState } from "react";
import ReactDOM from "react-dom";
import { useNavigate } from "react-router-dom";
import { useApp } from "../context/AppContext";
import { t } from "../utils/i18n";
import { MapPin, Repeat, Users, UserPlus, Video, Heart, Megaphone, ChevronRight, Home } from "lucide-react";
import CreateVenueModal from "./CreateVenueModal";
import { GardenIcon } from "./GardenIcons";

const initials = (name?: string) => (name || "?").split(" ").map(p => p[0]).join("").slice(0, 2).toUpperCase();

// Seul item du menu à sortir du traitement plat commun aux autres — une surface noire à part
// (pas juste une icône noire sur fond transparent comme le reste de la liste), avec un reflet
// argenté diagonal qui la traverse une fois à l'ouverture puis au survol. Jamais de teinte
// dorée/néon/scintillement continu — un seul passage discret, dans le même esprit de retenue que
// FAST_COLOR (styles/tokens.ts) : une unique exception à la charte noir/blanc/gris, jamais une
// nouvelle couleur, juste un jeu de lumière sur du noir.
function GardenMenuButton({ onClick, labelKey, subKey }: { onClick: () => void; labelKey: string; subKey: string }) {
  const { lang } = useApp();
  const [sweeping, setSweeping] = useState(false);
  const reducedMotion = () => window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
  useEffect(() => {
    if (reducedMotion()) return;
    const t = setTimeout(() => setSweeping(true), 400);
    return () => clearTimeout(t);
  }, []);
  return (
    <button
      onClick={onClick}
      onMouseEnter={() => { if (!reducedMotion()) setSweeping(true); }}
      onAnimationEnd={() => setSweeping(false)}
      style={{
        width: "100%", display: "flex", alignItems: "center", gap: 14, border: "none",
        padding: "13px 14px", borderRadius: 14, cursor: "pointer", textAlign: "left",
        backgroundColor: "#0a0a0a", position: "relative", overflow: "hidden", marginTop: 4,
      }}
    >
      {sweeping && (
        <span aria-hidden style={{
          position: "absolute", inset: 0,
          background: "linear-gradient(115deg, transparent 40%, rgba(255,255,255,0.30) 50%, transparent 60%)",
          backgroundSize: "250% 100%", animation: "garden-sheen 1.8s ease",
        }} />
      )}
      <GardenIcon size={20} color="#FFFFFF" />
      <span style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 14.5, fontWeight: 700, color: "#FFFFFF" }}>{t(lang, labelKey)}</div>
        <div style={{ fontSize: 12, color: "rgba(255,255,255,0.55)" }}>{t(lang, subKey)}</div>
      </span>
      <ChevronRight size={16} color="rgba(255,255,255,0.4)" />
      <style>{`
        @keyframes garden-sheen { from { background-position: 150% 0; } to { background-position: -100% 0; } }
      `}</style>
    </button>
  );
}

type MenuItem = { Icon: React.ComponentType<{ size?: number; color?: string; strokeWidth?: number }>; labelKey: string; subKey: string; onClick: () => void };

// Menu latéral profil — glisse depuis la droite, ~80% de l'écran, fond assombri. Vient en
// couche par-dessus la navigation existante (voir Layout.tsx pour les points d'ouverture) sans
// la remplacer. Thème strictement noir/blanc/gris — la touche dorée posée initialement ici a été
// retirée sur demande explicite du fondateur (jugée hors de la palette OLTODO), jamais une icône
// de section en couleur.
export default function SideMenu({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { user, lang } = useApp();
  const navigate = useNavigate();
  const [mounted, setMounted] = useState(false);
  const [visible, setVisible] = useState(false);
  const [showCreateVenue, setShowCreateVenue] = useState(false);

  useEffect(() => {
    if (open) {
      setMounted(true);
      const raf = requestAnimationFrame(() => setVisible(true));
      return () => cancelAnimationFrame(raf);
    } else {
      setVisible(false);
      // Filet de sécurité en plus de onTransitionEnd (démontage normal) : si l'évènement ne se
      // déclenche pas pour une raison ou une autre (onglet en arrière-plan, transition
      // interrompue), le panneau reste démonté après le délai de la transition CSS de toute
      // façon, plutôt qu'un overlay invisible qui bloquerait les clics indéfiniment.
      const timeout = setTimeout(() => setMounted(false), 350);
      return () => clearTimeout(timeout);
    }
  }, [open]);

  if (!mounted) return null;

  const go = (path: string) => { onClose(); navigate(path); };

  const items: MenuItem[] = [
    { Icon: Home, labelKey: "side_menu_my_addresses", subKey: "side_menu_my_addresses_sub", onClick: () => go("/my-addresses") },
    { Icon: MapPin, labelKey: "side_menu_my_places", subKey: "side_menu_my_places_sub", onClick: () => go("/my-venues") },
    { Icon: Repeat, labelKey: "side_menu_my_rituals", subKey: "side_menu_my_rituals_sub", onClick: () => go("/profile?tab=Rituels") },
    { Icon: Users, labelKey: "side_menu_my_communities", subKey: "side_menu_my_communities_sub", onClick: () => go("/profile?tab=Communautés") },
    { Icon: UserPlus, labelKey: "side_menu_my_friends", subKey: "side_menu_my_friends_sub", onClick: () => go("/profile?tab=Amis") },
    { Icon: Video, labelKey: "side_menu_my_lives", subKey: "side_menu_my_lives_sub", onClick: () => go("/my-lives") },
    { Icon: Heart, labelKey: "side_menu_my_connections", subKey: "side_menu_my_connections_sub", onClick: () => go("/notifications?tab=likes") },
    { Icon: Megaphone, labelKey: "side_menu_ad_tool", subKey: "side_menu_ad_tool_sub", onClick: () => go("/ad-tool") },
  ];

  // Portail direct sur document.body — même bug/fix que DatePromptSheet.tsx : un position:fixed
  // descendant du conteneur mobile de Layout.tsx (WebkitOverflowScrolling:"touch") se positionne
  // sur Safari iOS par rapport au contenu défilant plutôt qu'au vrai viewport.
  return ReactDOM.createPortal(
    <div
      onClick={onClose}
      onTransitionEnd={() => { if (!visible) setMounted(false); }}
      style={{ position: "fixed", inset: 0, backgroundColor: "rgba(0,0,0,0.5)", zIndex: 4000, opacity: visible ? 1 : 0, transition: "opacity 0.3s ease", pointerEvents: visible ? "auto" : "none" }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{
          position: "fixed", top: 0, right: 0, bottom: 0, width: "80vw", maxWidth: 360,
          backgroundColor: "white", boxShadow: "-8px 0 40px rgba(0,0,0,0.15)",
          transform: visible ? "translateX(0)" : "translateX(100%)", transition: "transform 0.3s ease",
          display: "flex", flexDirection: "column", overflowY: "auto",
        }}
      >
        <div style={{ display: "flex", justifyContent: "flex-end", padding: "16px 16px 0" }}>
          <button onClick={onClose} style={{ border: "none", background: "none", cursor: "pointer", fontSize: 20, color: "#999", padding: 4, lineHeight: 1 }}>✕</button>
        </div>

        {/* En-tête profil */}
        <div style={{ padding: "8px 24px 20px", borderBottom: "1px solid #F0F0F0" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 14 }}>
            <div style={{ width: 56, height: 56, borderRadius: "50%", overflow: "hidden", border: "2px solid #0a0a0a", display: "flex", alignItems: "center", justifyContent: "center", backgroundColor: "#0a0a0a", color: "white", fontWeight: 700, flexShrink: 0 }}>
              {user?.avatar_url ? <img src={user.avatar_url} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} /> : initials(user?.name)}
            </div>
            <div style={{ minWidth: 0 }}>
              <div style={{ fontSize: 17, fontWeight: 800, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{user?.name}</div>
              <button onClick={() => go("/profile")} style={{ border: "none", background: "none", padding: 0, cursor: "pointer", display: "flex", alignItems: "center", gap: 2, color: "#0a0a0a", fontSize: 13, fontWeight: 700 }}>
                {t(lang, "side_menu_see_profile")} <ChevronRight size={14} />
              </button>
            </div>
          </div>
          <button onClick={() => setShowCreateVenue(true)} style={{ width: "100%", display: "flex", alignItems: "center", gap: 10, border: "none", background: "none", padding: "10px 0", cursor: "pointer", textAlign: "left" }}>
            <span style={{ width: 34, height: 34, borderRadius: "50%", backgroundColor: "#F5F5F5", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
              <MapPin size={16} color="#0a0a0a" strokeWidth={1.8} />
            </span>
            <span style={{ flex: 1, fontSize: 14, fontWeight: 700, color: "#0a0a0a" }}>{t(lang, "side_menu_create_place")}</span>
            <ChevronRight size={16} color="#999" />
          </button>
        </div>

        {/* Liste principale */}
        <div style={{ padding: "8px 12px" }}>
          {items.map(({ Icon, labelKey, subKey, onClick }) => (
            <button key={labelKey} onClick={onClick} style={{ width: "100%", display: "flex", alignItems: "center", gap: 14, border: "none", background: "none", padding: "12px", borderRadius: 14, cursor: "pointer", textAlign: "left" }}
              onMouseEnter={e => (e.currentTarget.style.backgroundColor = "#F8F8F8")}
              onMouseLeave={e => (e.currentTarget.style.backgroundColor = "transparent")}
            >
              <Icon size={20} color="#0a0a0a" strokeWidth={1.8} />
              <span style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 14.5, fontWeight: 700, color: "#0a0a0a" }}>{t(lang, labelKey)}</div>
                <div style={{ fontSize: 12, color: "#999" }}>{t(lang, subKey)}</div>
              </span>
              <ChevronRight size={16} color="#ccc" />
            </button>
          ))}
          <GardenMenuButton
            labelKey="side_menu_garden" subKey="side_menu_garden_sub"
            onClick={() => go("/garden")}
          />
        </div>

        {/* Carte "OLTODO Creator" retirée de la V1 (lancement stores) : ce n'était qu'un teaser
            "bientôt disponible", il n'a rien à faire dans une première version publique. La route
            /premium et pages/Premium.tsx restent en place pour la réactiver d'un simple bloc ici
            le moment venu. */}
      </div>

      {showCreateVenue && (
        <div onClick={e => e.stopPropagation()}>
          <CreateVenueModal onClose={() => setShowCreateVenue(false)} onCreated={() => setShowCreateVenue(false)} />
        </div>
      )}
    </div>,
    document.body
  );
}
